import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/landing/Navbar";
import HeroSection from "@/components/landing/HeroSection";
import EmissionsEstimator from "@/components/landing/EmissionsEstimator";
import FeaturesSection from "@/components/landing/FeaturesSection";
import StatsSection from "@/components/landing/StatsSection";
import HowItWorksSection from "@/components/landing/HowItWorksSection";
import ProductDemoSection from "@/components/landing/ProductDemoSection";
import FAQSection from "@/components/landing/FAQSection";
import ArticlesSection from "@/components/landing/ArticlesSection";
import TestimonialsSection from "@/components/landing/TestimonialsSection";
import CTASection from "@/components/landing/CTASection";
import Footer from "@/components/landing/Footer";
import { useLanguage } from "@/contexts/LanguageContext";

// Strip markdown formatting (bold, links, bullets) so plain text lands
// in JSON-LD schema fields. Mirrors the helper in FAQSection but kept
// local so this page's structured data is self-contained.
const stripMarkdown = (text: string): string =>
  text
    .replace(/\*\*\[([^\]]+)\]\([^)]+\)\*\*/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/^[\s]*[•\-]\s*/gm, "")
    .replace(/\n{2,}/g, "\n\n")
    .trim();

const Index = () => {
  const { language, t } = useLanguage();

  const metadata = {
    en: {
      title: "Carbonwize | Audit-Grade Carbon Platform for Enterprises",
      description: "Scope 1–3 carbon accounting aligned with TGO CFO and ISO 14064. Trusted by 500+ enterprises for audit-ready ESG, climate disclosure, and compliance reporting.",
      locale: "en_US"
    },
    th: {
      title: "Carbonwize | ระบบจัดการคาร์บอนระดับองค์กร",
      description: "CFO Platform ที่ได้รับการรับรองโดย อบก. และ SET สำหรับการจัดทำบัญชีคาร์บอน Scope 1-3 ตามมาตรฐาน TGO CFO และ ISO 14064 พร้อมออกรายงานจากระบบและทวนสอบในที่เดียว",
      locale: "th_TH"
    }
  };

  // FAQPage schema — generated from the same translation keys that drive
  // the visible FAQ accordion, so the schema always matches on-page
  // content. Order matches FAQSection.
  const faqEntries = [
    { q: t("faq.q1"), a: t("faq.a1") },
    { q: t("faq.q2"), a: t("faq.a2") },
    { q: t("faq.q6"), a: t("faq.a6") },
    { q: t("faq.q3"), a: t("faq.a3") },
    { q: t("faq.q4"), a: t("faq.a4") },
    { q: t("faq.q5"), a: t("faq.a5") },
  ];
  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqEntries.map((f) => ({
      "@type": "Question",
      name: stripMarkdown(f.q),
      acceptedAnswer: {
        "@type": "Answer",
        text: stripMarkdown(f.a),
      },
    })),
  };

  // HowTo schema — describes the end-to-end Carbonwize workflow as
  // step-by-step content for Google AI Overviews. Steps map 1:1 to the
  // visible HowItWorksSection accordion (data → tracking → reports →
  // targets → projects), so we don't fabricate steps that don't exist
  // on the page.
  const howToSteps = [
    { title: t("howItWorks.step1Title"), desc: t("howItWorks.step1Desc") },
    { title: t("howItWorks.step2Title"), desc: t("howItWorks.step2Desc") },
    { title: t("howItWorks.step3Title"), desc: t("howItWorks.step3Desc") },
    { title: t("howItWorks.step4Title"), desc: t("howItWorks.step4Desc") },
    { title: t("howItWorks.step5Title"), desc: t("howItWorks.step5Desc") },
  ];
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name:
      language === "th"
        ? "วิธีจัดทำบัญชีคาร์บอน Scope 1, 2, 3 ขององค์กรด้วย Carbonwize"
        : "How to manage Scope 1, 2, 3 corporate carbon accounting with Carbonwize",
    description:
      language === "th"
        ? "ขั้นตอนการจัดทำบัญชีคาร์บอนระดับองค์กรตามมาตรฐาน TGO CFO, ISO 14064 และ GHG Protocol ตั้งแต่การเชื่อมต่อข้อมูลจนถึงการออกรายงานที่พร้อมทวนสอบ"
        : "Step-by-step workflow for enterprise carbon accounting aligned with TGO CFO, ISO 14064 and GHG Protocol — from connecting operational data to producing audit-ready Scope 1, 2, and 3 emissions reports.",
    inLanguage: language === "th" ? "th" : "en",
    totalTime: "PT30M",
    step: howToSteps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: stripMarkdown(s.title),
      text: stripMarkdown(s.desc),
      url: `https://carbonwize.io${language === "en" ? "/en" : ""}/#how-it-works`,
    })),
  };



  return (
    <>
      <SEOHead
        title={metadata[language].title}
        description={metadata[language].description}
        ogDescription={language === "en" ? "Enterprise carbon accounting platform trusted by 500+ organizations for Scope 1–3 reporting, audit readiness, ESG disclosure, and decarbonization planning." : undefined}
        twitterDescription={language === "en" ? "Scope 1–3 carbon accounting aligned with TGO CFO and ISO 14064. Trusted by 500+ enterprises." : undefined}
        ogImage="/og-cover.jpg"
        path="/"
        keywords="carbon accounting Thailand, enterprise carbon management, TGO CFO, ISO 14064, GHG Protocol, ESG reporting Thailand, climate disclosure software, audit-ready carbon accounting, SET carbon platform, sustainability software Thailand, ซอฟต์แวร์คาร์บอน, แพลตฟอร์มจัดการคาร์บอน, คาร์บอนฟุตพริ้นท์องค์กร, CFO องค์กร, TGO certified, GHG protocol, ก๊าซเรือนกระจก, รายงาน ESG, net zero Thailand, CSRD compliance, SEC climate disclosure, carbon footprint software, บริษัทจดทะเบียน SET, ตลาดหลักทรัพย์, คาร์บอนเครดิต"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Carbonwize",
            "url": "https://www.carbonwize.io",
            "logo": "https://www.carbonwize.io/logo.png",
            "description": "Scope 1-3 carbon accounting aligned with TGO & ISO 14064. Trusted by 500+ enterprises for compliance-ready reports in minutes.",
            "sameAs": [
              "https://www.linkedin.com/company/carbonwize",
              "https://www.crunchbase.com/organization/carbonwize"
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Carbonwize",
            "alternateName": "คาร์บอนไวซ์",
            "url": "https://carbonwize.io",
            "logo": {
              "@type": "ImageObject",
              "url": "https://carbonwize.io/carbonwize-logo.png",
              "width": 200,
              "height": 60
            },
            "image": "https://carbonwize.io/og-thumbnail.png",
            "description": "Automate carbon accounting with enterprise-grade compliance, precision, and automation. Trusted by 500+ enterprises.",
            "slogan": "ซอฟต์แวร์คาร์บอน อันดับ 1 ประเทศไทย",
            "foundingDate": "2020",
            "areaServed": [
              { "@type": "Country", "name": "Thailand" },
              { "@type": "Place", "name": "Southeast Asia" },
              { "@type": "Place", "name": "Asia Pacific" }
            ],
            "address": {
              "@type": "PostalAddress",
              "addressCountry": "TH",
              "addressLocality": "Bangkok",
              "addressRegion": "Bangkok"
            },
            "contactPoint": [
              {
                "@type": "ContactPoint",
                "contactType": "sales",
                "availableLanguage": ["Thai", "English"],
                "url": "https://carbonwize.io/contact-us"
              },
              {
                "@type": "ContactPoint",
                "contactType": "customer support",
                "availableLanguage": ["Thai", "English"]
              }
            ],
            "sameAs": [
              "https://www.linkedin.com/company/carbonwize",
              "https://www.facebook.com/carbonwize",
              "https://www.crunchbase.com/organization/carbonwize"
            ],
            "knowsAbout": [
              "Carbon Accounting",
              "GHG Protocol",
              "ISO 14064",
              "ESG Reporting",
              "Carbon Footprint",
              "Net Zero Strategy",
              "CSRD Compliance",
              "TGO Standards"
            ],
            "hasCredential": [
              {
                "@type": "EducationalOccupationalCredential",
                "credentialCategory": "certification",
                "name": "TGO Certified Partner"
              },
              {
                "@type": "EducationalOccupationalCredential",
                "credentialCategory": "partnership",
                "name": "SET Official Partner"
              }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Carbonwize Carbon Management Platform",
            "alternateName": "Carbonwize ซอฟต์แวร์คาร์บอน",
            "description": metadata[language].description,
            "url": "https://carbonwize.io",
            "image": "https://carbonwize.io/og-thumbnail.png",
            "brand": {
              "@type": "Brand",
              "name": "Carbonwize",
              "logo": "https://carbonwize.io/carbonwize-logo.png"
            },
            "category": "Business Software > Environmental Management > Carbon Accounting",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "THB",
              "priceValidUntil": "2025-12-31",
              "availability": "https://schema.org/InStock",
              "description": "นัดสาธิตฟรี ไม่มีค่าใช้จ่าย",
              "seller": {
                "@type": "Organization",
                "name": "Carbonwize"
              }
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "reviewCount": "500",
              "bestRating": "5",
              "worstRating": "1"
            },
            "review": [
              {
                "@type": "Review",
                "reviewRating": {
                  "@type": "Rating",
                  "ratingValue": "5",
                  "bestRating": "5"
                },
                "author": {
                  "@type": "Person",
                  "name": "คุณจุฑากาญจน์ ศิริวัฒน์"
                },
                "reviewBody": "ใช้งานง่าย ตอบโจทย์การรายงานคาร์บอนฟุตพริ้นท์ขององค์กร"
              }
            ],
            "audience": {
              "@type": "BusinessAudience",
              "audienceType": "SET Listed Companies, Large Enterprises, ESG-focused Organizations"
            },
            "additionalProperty": [
              {
                "@type": "PropertyValue",
                "name": "Supported Standards",
                "value": "TGO, ISO 14064, GHG Protocol, CSRD, SEC Climate Disclosure"
              },
              {
                "@type": "PropertyValue",
                "name": "Active Users",
                "value": "500+ Organizations"
              },
              {
                "@type": "PropertyValue",
                "name": "Supported Languages",
                "value": "Thai, English"
              }
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "Carbonwize",
            "applicationCategory": "BusinessApplication",
            "applicationSubCategory": "Environmental Management Software",
            "description": metadata[language].description,
            "operatingSystem": "Web-based (All platforms)",
            "inLanguage": ["th", "en"],
            "softwareVersion": "2.0",
            "featureList": [
              "Automated Carbon Calculation",
              "Real-time Dashboard & Analytics",
              "Multi-standard Reporting (TGO, ISO 14064, GHG Protocol)",
              "Scope 1, 2, 3 Emissions Tracking",
              "ESG Report Generation",
              "Third-party Verification Support"
            ],
            "screenshot": "https://carbonwize.io/dashboard-screenshot.png",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "THB",
              "description": "Free Demo Available"
            },
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "ratingCount": "500",
              "bestRating": "5"
            },
            "provider": {
              "@type": "Organization",
              "name": "Carbonwize",
              "url": "https://carbonwize.io"
            }
          },
          // FAQPage schema is emitted by <FAQSection /> from the live FAQ
          // content so the schema always matches what users see.

          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Carbonwize",
            "alternateName": "คาร์บอนไวซ์",
            "url": "https://carbonwize.io",
            "inLanguage": ["th", "en"],
            "publisher": {
              "@type": "Organization",
              "name": "Carbonwize"
            }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://carbonwize.io"
              }
            ]
          },
          faqPageSchema,
          howToSchema
        ]}
      />

      <main className="min-h-screen">
        <Navbar />
        <HeroSection />
        <EmissionsEstimator />
        <FeaturesSection />
        <HowItWorksSection />
        <ProductDemoSection />
        <StatsSection />
        <TestimonialsSection />
        <CTASection />
        <ArticlesSection />
        <FAQSection />
        <Footer />
      </main>
    </>
  );
};

export default Index;
